import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosInstance';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import StatusBadge from '../components/StatusBadge';

export default function Queue() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [errorDetails, setErrorDetails] = useState({});
  const [retryingJobId, setRetryingJobId] = useState(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await axios.get('api/transly/jobs/');
        setJobs(res.data);
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleExpand = async (jobId) => {
    if (expandedJobId === jobId) { setExpandedJobId(null); return; }
    setExpandedJobId(jobId);
    if (!errorDetails[jobId]) {
      try {
        const res = await axios.get('api/transly/jobs/', { params: { id: jobId } });
        setErrorDetails((prev) => ({ ...prev, [jobId]: res.data.failedEntries || [] }));
      } catch {}
    }
  };

  const handleRetry = async (jobId) => {
    setRetryingJobId(jobId);
    try {
      await axios.post('api/transly/jobs/', { jobId });
    } catch (err) {
      alert('Retry failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setRetryingJobId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground mb-6">Translation Queue</h1>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Locale</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Progress</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Started</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Completed</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const total = job.total_resources || 0;
                const done = (job.completed_count || 0) + (job.failed_count || 0);
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <React.Fragment key={job.id}>
                    <tr className="border-b border-border hover:bg-accent transition-colors">
                      <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                      <td className="px-4 py-3 uppercase text-foreground">{job.target_locale}</td>
                      <td className="px-4 py-3 text-foreground">{job.resource_type}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-muted rounded-full h-1.5">
                            <div className="h-1.5 rounded-full bg-primary" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {job.completed_count || 0}/{total}
                            {(job.failed_count || 0) > 0 && (
                              <button onClick={() => handleExpand(job.id)} className="ml-1 text-destructive hover:underline">
                                ({job.failed_count} failed)
                              </button>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(job.started_at)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(job.completed_at)}</td>
                      <td className="px-4 py-3">
                        {job.failed_count > 0 && job.status !== 'in_progress' && (
                          <Button variant="outline" size="sm" onClick={() => handleRetry(job.id)} disabled={retryingJobId === job.id}>
                            {retryingJobId === job.id ? 'Retrying...' : 'Retry Failed'}
                          </Button>
                        )}
                      </td>
                    </tr>
                    {expandedJobId === job.id && errorDetails[job.id] && (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 bg-destructive/5">
                          <div className="text-sm">
                            <h4 className="font-medium text-destructive mb-2">Failed entries:</h4>
                            {errorDetails[job.id].slice(0, 20).map((e, i) => (
                              <div key={i} className="text-xs text-destructive/80 mb-1">
                                <span className="font-mono">{e.resource_id}</span> - {e.error_message}
                              </div>
                            ))}
                            {errorDetails[job.id].length > 20 && (
                              <p className="text-xs text-muted-foreground mt-1">...and {errorDetails[job.id].length - 20} more</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
